import React, { useState } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import './geo_growth.css';

// # Chart options for country distribution
const countryPieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        font: { size: 12 },
        // # Only show top 10 countries in legend
        filter: (legendItem, data) => {
          return data.datasets[0].data.indexOf(legendItem.dataIndex) < 10;
        }
      }
    },
    title: {
      display: true,
      text: 'User Distribution by Country - Top 10',
      font: { size: 16, weight: 'bold' }
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const label = context.label || '';
          const value = context.raw || 0;
          const percentage = context.dataset.percentages[context.dataIndex];
          return `${label}: ${value} users (${percentage}%)`;
        }
      }
    }
  }
};

const GeoGrowth = ({ analyticsData }) => {
  const [countryFilter, setCountryFilter] = useState('');
  const [countrySortConfig, setCountrySortConfig] = useState({
    key: 'count',
    direction: 'desc'
  });

  // # Prepare data for country pie chart
  const getCountryPieData = () => {
    const sortedCountries = getSortedCountries();
    const topCountries = sortedCountries.slice(0, 10); // # Get top 10 countries
    const otherCountries = sortedCountries.slice(10); // # Remaining countries

    // # Calculate total for other countries
    const otherTotal = otherCountries.reduce((sum, country) => sum + country.count, 0);
    const otherPercentage = ((otherTotal / analyticsData.totalUsers) * 100).toFixed(1);

    return {
      labels: [...topCountries.map(c => c.country), 'Others'],
      datasets: [{
        data: [...topCountries.map(c => c.count), otherTotal],
        percentages: [...topCountries.map(c => c.percentage), otherPercentage],
        backgroundColor: [
          'pink', '#3498db', '#e74c3c', '#2ecc71', '#f1c40f',
          '#9b59b6', '#1abc9c', '#34495e', '#e67e22', '#16a085',
          '#8e44ad' // # For "Others"
        ],
        borderWidth: 1
      }]
    };
  };

  // # Sort and filter countries
  const getSortedCountries = () => {
    const countries = Object.entries(analyticsData.usersByCountry)
      .map(([country, count]) => ({
        country,
        count,
        percentage: ((count / analyticsData.totalUsers) * 100).toFixed(1)
      }))
      .filter(item => 
        item.country.toLowerCase().includes(countryFilter.toLowerCase())
      );

    return countries.sort((a, b) => {
      if (countrySortConfig.key === 'country') {
        return countrySortConfig.direction === 'asc' 
          ? a.country.localeCompare(b.country)
          : b.country.localeCompare(a.country);
      } else {
        const aValue = countrySortConfig.key === 'count' ? a.count : parseFloat(a.percentage);
        const bValue = countrySortConfig.key === 'count' ? b.count : parseFloat(b.percentage);
        return countrySortConfig.direction === 'asc' 
          ? aValue - bValue 
          : bValue - aValue;
      }
    });
  };

  // # Handle column sort
  const handleSort = (key) => {
    setCountrySortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // # Get sort direction indicator
  const getSortIndicator = (columnKey) => {
    if (countrySortConfig.key !== columnKey) return '↕';
    return countrySortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="grth_geo_container">
      <h2>Geographic Distribution</h2>
      
      {/* Country Distribution Charts */}
      <div className="grth_geo_charts-grid">
        {/* Pie Chart */}
        <div className="grth_geo_chart-container">
          <div className="grth_geo_pie-chart">
            <Pie data={getCountryPieData()} options={countryPieOptions} />
          </div>
        </div>

        {/* Bar Chart */}
        <div className="grth_geo_chart-container">
          <div className="grth_geo_bar-chart">
            <Bar
              data={{
                labels: getSortedCountries().slice(0, 10).map(item => item.country),
                datasets: [
                  {
                    label: 'Users by Country',
                    data: getSortedCountries().slice(0, 10).map(item => item.count),
                    backgroundColor: '#0080ff',
                    borderColor: '#0066cc',
                    borderWidth: 1,
                    borderRadius: 4,
                    barThickness: 30,
                    maxBarThickness: 40
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  title: {
                    display: true,
                    text: 'Top 10 Countries by User Count',
                    font: { size: 16, weight: 'bold' },
                    padding: { top: 10, bottom: 20 }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const value = context.raw;
                        const percentage = ((value / analyticsData.totalUsers) * 100).toFixed(1);
                        return `${value} users (${percentage}%)`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Number of Users',
                      font: { weight: 'bold' }
                    },
                    ticks: { precision: 0 }
                  },
                  x: {
                    title: {
                      display: true,
                      text: 'Country',
                      font: { weight: 'bold' }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Country Table */}
      <div className="grth_geo_chart-container">
        <h3>Users by Country</h3>
        <div className="grth_geo_table-controls">
          <input
            type="text"
            placeholder="Search countries..."
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="grth_geo_search-input"
          />
        </div>
        <div className="grth_geo_country-stats">
          <table className="grth_geo_stats-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('country')} className="grth_geo_sortable-header">
                  Country {getSortIndicator('country')}
                </th>
                <th onClick={() => handleSort('count')} className="grth_geo_sortable-header">
                  Users {getSortIndicator('count')}
                </th>
                <th onClick={() => handleSort('percentage')} className="grth_geo_sortable-header">
                  Percentage {getSortIndicator('percentage')}
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedCountries().map(({ country, count, percentage }) => (
                <tr key={country}>
                  <td>{country}</td>
                  <td>{count}</td>
                  <td>{percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GeoGrowth;
